import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Columns3 } from 'lucide-react'
import { Button } from '../Button'
import { Checkbox } from '../Checkbox'
import { POPOVER_Z_INDEX } from '../z-index'

const PANEL_WIDTH = 256
const PANEL_MAX_HEIGHT = 360
const PANEL_GAP = 6

interface TableColumnOption<Key extends string> {
    key: Key
    label: string
    locked?: boolean
}

interface TableColumnsMenuProps<Key extends string> {
    columns: Array<TableColumnOption<Key>>
    visibleColumns: Key[]
    allKeys: Key[]
    /** What Reset restores — a table's default columns. Defaults to every available column. */
    resetKeys?: Key[]
    onChange: (next: Key[]) => void
    /** The trigger's height, to line up with the other controls of a toolbar. */
    size?: 'sm' | 'md'
}

export const TableColumnsMenu = <Key extends string>({
    columns,
    visibleColumns,
    allKeys,
    resetKeys = allKeys,
    onChange,
    size = 'sm',
}: TableColumnsMenuProps<Key>) => {
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState<CSSProperties | null>(null)
    const ref = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    // The panel is portaled to <body> with fixed positioning: tables sit in cards that clip overflow
    // (rounded corners), which cut the menu off. Right-aligned to the button, flipped above it when
    // there is not enough room below.
    const measure = useCallback(() => {
        const rect = ref.current?.getBoundingClientRect()
        if (!rect) return
        const spaceBelow = window.innerHeight - rect.bottom
        const flipUp = spaceBelow < PANEL_MAX_HEIGHT + PANEL_GAP && rect.top > spaceBelow
        const left = Math.max(8, Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8))
        setPosition(flipUp ? { left, bottom: window.innerHeight - rect.top + PANEL_GAP } : { left, top: rect.bottom + PANEL_GAP })
    }, [])

    useLayoutEffect(() => {
        if (open) measure()
    }, [open, measure])

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (event: MouseEvent) => {
            if (!(event.target instanceof Node)) return
            if (ref.current?.contains(event.target) || panelRef.current?.contains(event.target)) return
            setOpen(false)
        }
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
        window.addEventListener('resize', measure)
        window.addEventListener('scroll', measure, true)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscape)
            window.removeEventListener('resize', measure)
            window.removeEventListener('scroll', measure, true)
        }
    }, [open, measure])

    const toggle = (column: TableColumnOption<Key>) => {
        if (column.locked) return
        if (visibleColumns.includes(column.key)) {
            const next = visibleColumns.filter((key) => key !== column.key)
            if (next.length === 0) return
            onChange(next)
            return
        }
        onChange(allKeys.filter((key) => visibleColumns.includes(key) || key === column.key))
    }

    return (
        <div className="relative" ref={ref}>
            <Button
                variant="secondary"
                size={size}
                onClick={() => setOpen((current) => !current)}
                prefix={<Columns3 className="size-4" strokeWidth={1.5} />}
                suffix={
                    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-soft px-1.5 text-[11px] font-semibold text-accent-soft-foreground">
                        {visibleColumns.length}
                    </span>
                }
            >
                Columns
            </Button>
            {open &&
                position &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{ ...position, position: 'fixed', width: PANEL_WIDTH, maxHeight: PANEL_MAX_HEIGHT, zIndex: POPOVER_Z_INDEX }}
                        className="flex flex-col rounded-xl bg-surface p-1 shadow-overlay"
                    >
                        <div className="flex-1 overflow-auto p-1">
                            {columns.map((column) => (
                                <div
                                    key={column.key}
                                    onClick={() => toggle(column)}
                                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-default ${
                                        column.locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                                    }`}
                                >
                                    <Checkbox checked={visibleColumns.includes(column.key)} disabled={column.locked} ariaLabel={column.label} />
                                    <span className="text-sm text-foreground">{column.label}</span>
                                    {column.locked && (
                                        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">Locked</span>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-2 pb-1 pt-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    onChange(resetKeys)
                                    setOpen(false)
                                }}
                            >
                                Reset
                            </Button>
                            <Button size="sm" onClick={() => setOpen(false)}>
                                Done
                            </Button>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    )
}
